import { useRef, useState } from 'react'
import { AlertCircle, Banknote, Check, CreditCard, X } from 'lucide-react'
import { openRazorpayCheckout } from '@/services/payments/razorpayCheckout'
import { usePaymentStore } from '@/store/paymentStore'
import { toast } from '@/store/toastStore'
import type {
  CreatePaymentInput,
  PaymentCurrency,
  PaymentMethod,
} from '@/types/payment.types'
import { PAYMENT_METHOD_LABELS, PAYMENT_METHOD_OPTIONS } from '@/types/payment.types'

type EntryMode = 'manual' | 'online'

interface PaymentModalProps {
  isOpen: boolean
  onClose: () => void
  customerId?: string
  customerName?: string
  onSuccess?: (paymentId: string) => void
  onPending?: () => void
}

interface FormData {
  customerId: string
  amount: string
  currency: PaymentCurrency
  paymentMethod: PaymentMethod
  description: string
}

const CURRENCIES: PaymentCurrency[] = ['INR', 'USD', 'EUR', 'GBP']

function initialData(customerId?: string): FormData {
  return {
    customerId: customerId ?? '',
    amount: '',
    currency: 'INR',
    paymentMethod: 'upi',
    description: '',
  }
}

function newIdempotencyKey(): string {
  const random = globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}_${Math.random().toString(36).slice(2)}`
  return `client_checkout_${random}`
}

export default function PaymentModal({
  isOpen,
  onClose,
  customerId,
  customerName,
  onSuccess,
  onPending,
}: PaymentModalProps) {
  const { addPayment, startOnlinePayment, verifyOnlinePayment, isLoading } =
    usePaymentStore()
  const [mode, setMode] = useState<EntryMode>('online')
  const [formData, setFormData] = useState<FormData>(initialData(customerId))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [touched, setTouched] = useState<Set<string>>(new Set())
  const idempotencyKey = useRef(newIdempotencyKey())
  const lockCustomer = Boolean(customerId)

  const reset = () => {
    setMode('online')
    setFormData(initialData(customerId))
    setErrors({})
    setTouched(new Set())
    idempotencyKey.current = newIdempotencyKey()
  }

  const close = () => {
    reset()
    onClose()
  }

  const selectMode = (next: EntryMode) => {
    setMode(next)
    if (next === 'online') {
      setFormData((current) => ({ ...current, currency: 'INR' }))
    }
  }

  const validate = () => {
    const next: Record<string, string> = {}
    if (!lockCustomer && !formData.customerId.trim()) {
      next.customerId = 'Customer ID is required'
    }
    const amount = Number(formData.amount)
    if (!formData.amount.trim()) next.amount = 'Amount is required'
    else if (!Number.isFinite(amount) || amount <= 0) {
      next.amount = 'Enter an amount greater than zero'
    } else if (!Number.isInteger(amount * 100)) {
      next.amount = 'Use no more than two decimal places'
    }
    setErrors(next)
    setTouched(new Set(['customerId', 'amount']))
    return Object.keys(next).length === 0
  }

  const complete = (paymentId: string) => {
    reset()
    onClose()
    onSuccess?.(paymentId)
  }

  const submitManual = async () => {
    const input: CreatePaymentInput = {
      customerId: formData.customerId.trim(),
      amount: Number(formData.amount),
      currency: formData.currency,
      paymentMethod: formData.paymentMethod,
      gateway: 'manual',
      description: formData.description.trim() || undefined,
    }
    try {
      const payment = await addPayment(input)
      toast.success(
        'Offline payment recorded',
        'It remains pending until reconciled by authorized staff.'
      )
      complete(payment.id)
    } catch (error) {
      toast.error(
        'Could not record payment',
        error instanceof Error ? error.message : 'Please try again.'
      )
    }
  }

  const submitOnline = async () => {
    let order
    try {
      order = await startOnlinePayment(
        {
          customerId: formData.customerId.trim(),
          amount: Number(formData.amount),
          currency: 'INR',
          paymentMethod: formData.paymentMethod,
          description: formData.description.trim() || undefined,
        },
        idempotencyKey.current
      )
      onPending?.()
    } catch (error) {
      toast.error(
        'Could not start Razorpay checkout',
        error instanceof Error ? error.message : 'Please try again.'
      )
      return
    }

    let proof
    try {
      proof = await openRazorpayCheckout(order)
    } catch (error) {
      toast.error(
        'Razorpay checkout did not complete',
        error instanceof Error ? error.message : 'The payment remains pending.'
      )
      return
    }
    if (!proof) {
      toast.info(
        'Checkout closed',
        'The payment remains pending and can be retried safely.'
      )
      return
    }

    try {
      const payment = await verifyOnlinePayment(proof)
      toast.success('Payment confirmed', 'Razorpay verified the payment successfully.')
      complete(payment.id)
    } catch (error) {
      toast.warning(
        'Payment awaiting confirmation',
        error instanceof Error
          ? error.message
          : 'Refresh provider status before taking further action.'
      )
    }
  }

  const submit = async () => {
    if (!validate()) return
    if (mode === 'manual') await submitManual()
    else await submitOnline()
  }

  if (!isOpen) return null
  const showError = (field: string) => touched.has(field) && errors[field]

  return (
    <div
      className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payment-modal-title"
    >
      <div className="bg-white dark:bg-surface-dark rounded-2xl shadow-2xl max-w-2xl w-full max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-5 border-b border-surface-overlay bg-gradient-to-r from-teal-50 to-amber-50 dark:from-teal-950/30 dark:to-amber-950/20">
          <div>
            <h2 id="payment-modal-title" className="text-xl font-semibold text-text-primary">
              Add client payment
            </h2>
            <p className="text-sm text-text-secondary mt-1">
              Choose how this payment enters Tax Hive.
            </p>
          </div>
          <button onClick={close} className="btn btn-ghost btn-circle btn-sm" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Payment entry method">
            <ModeButton
              active={mode === 'online'}
              icon={<CreditCard className="w-5 h-5 text-teal-700 dark:text-teal-300" />}
              title="Collect online"
              detail="Open secure Razorpay Checkout and verify with the server."
              tone="teal"
              onClick={() => selectMode('online')}
            />
            <ModeButton
              active={mode === 'manual'}
              icon={<Banknote className="w-5 h-5 text-amber-700 dark:text-amber-300" />}
              title="Record offline"
              detail="Log cash, bank transfer, or another externally received payment."
              tone="amber"
              onClick={() => selectMode('manual')}
            />
          </div>

          <div>
            <label htmlFor="payment-customer-id" className="block text-sm font-medium text-text-primary mb-1">
              Customer {!lockCustomer && <span className="text-red-500">*</span>}
            </label>
            {lockCustomer ? (
              <div className="input input-bordered w-full flex items-center bg-surface-overlay/40">
                {customerName ?? `Customer #${customerId}`}
              </div>
            ) : (
              <>
                <input
                  id="payment-customer-id"
                  type="text"
                  value={formData.customerId}
                  onChange={(event) => setFormData((current) => ({ ...current, customerId: event.target.value }))}
                  onBlur={() => setTouched((current) => new Set(current).add('customerId'))}
                  placeholder="Customer identifier"
                  className={`input input-bordered w-full ${showError('customerId') ? 'border-red-500' : ''}`}
                  aria-invalid={Boolean(showError('customerId'))}
                />
                {showError('customerId') && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" /> {errors.customerId}
                  </p>
                )}
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="payment-amount" className="block text-sm font-medium text-text-primary mb-1">
                Amount <span className="text-red-500">*</span>
              </label>
              <input
                id="payment-amount"
                type="number"
                min="0.01"
                step="0.01"
                value={formData.amount}
                onChange={(event) => setFormData((current) => ({ ...current, amount: event.target.value }))}
                onBlur={() => setTouched((current) => new Set(current).add('amount'))}
                placeholder="0.00"
                className={`input input-bordered w-full ${showError('amount') ? 'border-red-500' : ''}`}
                aria-invalid={Boolean(showError('amount'))}
              />
              {showError('amount') && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" /> {errors.amount}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="payment-currency" className="block text-sm font-medium text-text-primary mb-1">Currency</label>
              {mode === 'online' ? (
                <div className="input input-bordered w-full flex items-center bg-surface-overlay/40">INR</div>
              ) : (
                <select
                  id="payment-currency"
                  value={formData.currency}
                  onChange={(event) => setFormData((current) => ({ ...current, currency: event.target.value as PaymentCurrency }))}
                  className="input input-bordered w-full"
                >
                  {CURRENCIES.map((currency) => <option key={currency}>{currency}</option>)}
                </select>
              )}
            </div>
          </div>

          <div>
            <label htmlFor="payment-method" className="block text-sm font-medium text-text-primary mb-1">Payment method</label>
            <select
              id="payment-method"
              value={formData.paymentMethod}
              onChange={(event) => setFormData((current) => ({ ...current, paymentMethod: event.target.value as PaymentMethod }))}
              className="input input-bordered w-full"
            >
              {PAYMENT_METHOD_OPTIONS.map((method) => (
                <option key={method} value={method}>{PAYMENT_METHOD_LABELS[method]}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="payment-description" className="block text-sm font-medium text-text-primary mb-1">Description</label>
            <textarea
              id="payment-description"
              value={formData.description}
              onChange={(event) => setFormData((current) => ({ ...current, description: event.target.value }))}
              rows={3}
              maxLength={500}
              placeholder="What is this payment for?"
              className="input input-bordered w-full"
            />
          </div>

          {mode === 'online' && (
            <p className="rounded-lg bg-teal-50 dark:bg-teal-950/30 px-4 py-3 text-sm text-teal-800 dark:text-teal-200">
              Tax Hive never receives card or UPI credentials. Razorpay hosts checkout; Tax Hive confirms the signed result with its server.
            </p>
          )}
        </div>

        <div className="flex items-center justify-between px-6 py-5 border-t border-surface-overlay">
          <button onClick={close} className="btn btn-ghost">Cancel</button>
          <button onClick={submit} disabled={isLoading} className="btn btn-primary flex items-center gap-2">
            {isLoading ? (
              <><span className="loading loading-spinner loading-sm" />Working...</>
            ) : (
              <><Check className="w-4 h-4" />{mode === 'online' ? 'Continue to Razorpay' : 'Record offline payment'}</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

interface ModeButtonProps {
  active: boolean
  icon: React.ReactNode
  title: string
  detail: string
  tone: 'teal' | 'amber'
  onClick: () => void
}

function ModeButton({ active, icon, title, detail, tone, onClick }: ModeButtonProps) {
  const activeStyle = tone === 'teal'
    ? 'border-teal-600 bg-teal-50 dark:bg-teal-950/30'
    : 'border-amber-600 bg-amber-50 dark:bg-amber-950/30'
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={`text-left rounded-xl border-2 p-4 transition-colors ${active ? activeStyle : 'border-surface-overlay hover:border-slate-400'}`}
    >
      <span className="block mb-2">{icon}</span>
      <span className="block font-semibold text-text-primary">{title}</span>
      <span className="block text-sm text-text-secondary mt-1">{detail}</span>
    </button>
  )
}
